import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MeetingRoom } from '../components/Meeting/MeetingRoom';
import { meetingService, MeetingTokenResponse } from '../services/api/meetingService';
import { authService } from '../services/api/authService';
import './PatientMeetingRoomPage.scss';

export function PatientMeetingRoomPage() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();

  const [meetingAuth, setMeetingAuth] = useState<MeetingTokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMeetingToken = async () => {
      if (!meetingId) {
        setError('Meeting ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log('🎥 Fetching meeting token for:', meetingId);

        const tokenData = await meetingService.generateToken({
          meeting_id: meetingId,
          patient: 1,
        });

        console.log('✅ Meeting token received:', tokenData);
        setMeetingAuth(tokenData);
      } catch (err) {
        console.error('❌ Failed to fetch meeting token:', err);
        const message = err instanceof Error ? err.message : 'Failed to join meeting';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeetingToken();
  }, [meetingId]);

  const handleLeave = useCallback(async () => {
    console.log('👋 Leaving meeting');
    // Logout the user
    await authService.logout();
    // Force a full page reload to clear all state and redirect to login
    window.location.href = '/patient/info';
  }, []);

  const handleError = useCallback((errorMessage: string) => {
    console.error('🚨 Meeting error:', errorMessage);
    setError(errorMessage);
  }, []);

  if (isLoading) {
    return (
      <div className="meeting-room-page" data-component="PatientMeetingRoomPage">
        <div className="meeting-room-page__container">
          <div className="meeting-room-page__loading">
            <div className="animate-pulse">
              <img
                src="/aigp-logo.png"
                alt="AIGP Health"
                className="h-20 w-auto mb-4"
              />
            </div>
            <p className="text-lg font-medium text-white">Joining Meeting...</p>
            <span className="text-sm text-white/70">Please wait while we connect you</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !meetingAuth) {
    return (
      <div className="meeting-room-page" data-component="PatientMeetingRoomPage">
        <div className="meeting-room-page__container">
          <div className="meeting-room-page__error">
            <img
              src="/aigp-logo.png"
              alt="AIGP Health"
              className="h-20 w-auto mb-4"
            />
            <h2 className="text-xl font-semibold text-red-400 mb-2">Unable to Join Meeting</h2>
            <p className="text-white/80 mb-6">{error || 'Failed to connect to meeting'}</p>
            <button
              onClick={handleLeave}
              className="px-6 py-3 bg-gradient-to-r from-[#356BAD] to-[#356BAD] text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-room-page" data-component="PatientMeetingRoomPage">
      <div className="meeting-room-page__container">
        <MeetingRoom
          initial={meetingAuth}
          onLeave={handleLeave}
          onError={handleError}
          displayMode="page"
        />
      </div>
    </div>
  );
}
